import { useMemo, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AdminCourse, CourseStatus, getCourseStatus } from '../../../../models/CourseModels';

/** Filtro por status vindo da URL (`?status=`). */
export type StatusFilter = 'all' | CourseStatus;

/** Chaves de busca e filtro gravadas na URL (query params). */
const SEARCH_KEY = 'q';
const STATUS_KEY = 'status';

const parseStatus = (value: string | null): StatusFilter => {
  if (value === 'active' || value === 'soon' || value === 'inactive') return value;
  return 'all';
};

/**
 * Estado de busca e filtro da lista de cursos, sincronizado com a URL.
 * A filtragem é feita no cliente sobre o que já foi carregado (o backend não
 * oferece busca/filtro/ordenação). Guardar na URL permite compartilhar e
 * voltar.
 */
export const useCourseListFilter = (courses: AdminCourse[]) => {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [search, setSearch] = useState(params.get(SEARCH_KEY) ?? '');
  const [status, setStatus] = useState<StatusFilter>(parseStatus(params.get(STATUS_KEY)));

  // Reflete mudanças na URL em uma mesma URL limpa, sem stack extra.
  const syncUrl = useCallback(
    (nextSearch: string, nextStatus: StatusFilter) => {
      const next = new URLSearchParams();
      if (nextSearch) next.set(SEARCH_KEY, nextSearch);
      if (nextStatus !== 'all') next.set(STATUS_KEY, nextStatus);
      const qs = next.toString();
      history.replace({ search: qs ? `?${qs}` : '' });
    },
    [history],
  );

  const onSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      syncUrl(value, status);
    },
    [status, syncUrl],
  );

  const onStatusChange = useCallback(
    (value: StatusFilter) => {
      setStatus(value);
      syncUrl(search, value);
    },
    [search, syncUrl],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    return courses.filter((course) => {
      if (status !== 'all' && getCourseStatus(course) !== status) return false;
      if (q && !course.title.toLocaleLowerCase().includes(q)) return false;
      return true;
    });
  }, [courses, search, status]);

  return { search, status, filtered, onSearchChange, onStatusChange };
};
